import { useState } from "react";
import type { DefaultValues, FieldValues } from "react-hook-form";
import { staticText } from "../../statics/static-text";
import { FormSubmissionData } from "../form/Form.Parts";
import type { Field, FormLayout, FormSection } from "../form/Form.Parts";

// TEXT
export const text = staticText.form;

// STYLES

// DATA

// TYPES
export interface Props<T extends FieldValues> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  submitButtonText?: string;
  cancelButtonText?: string;
  fields?: Field<T>[];
  sections?: FormSection<T>[];
  defaultValues?: DefaultValues<T>;
  layout?: FormLayout;
  width?: string;
  disableWhenInvalid?: boolean;
  onSubmit: (
    data: FormSubmissionData<T>,
  ) => void | false | Promise<void | false>;
  onCancel?: () => void;
}

export type FormDialogConfig<T extends FieldValues> = {
  title?: string;
  description?: string;
  submitButtonText?: string;
  cancelButtonText?: string;
  fields?: Field<T>[];
  sections?: FormSection<T>[];
  defaultValues?: DefaultValues<T>;
  layout?: FormLayout;
};

export type UseFormDialogReturn<T extends FieldValues> = {
  isOpen: boolean;
  config: FormDialogConfig<T>;
  openFormDialog: (
    formConfig: FormDialogConfig<T>,
  ) => Promise<FormSubmissionData<T> | undefined>;
  handleSubmit: (data: FormSubmissionData<T>) => void;
  handleCancel: () => void;
  handleOpenChange: (open: boolean) => void;
  formDialogProps: Omit<Props<T>, "onSubmit"> & {
    onSubmit: (data: FormSubmissionData<T>) => void;
  };
};

// CONSTANTS
export const DEFAULT_WIDTH = "60rem";

// FUNCTIONS
export const focusFirstInput = (e: Event) => {
  e.preventDefault();
  const input = (e.target as HTMLElement).querySelector<HTMLElement>(
    "input, textarea, select",
  );
  input?.focus();
};

export const useFormDialog = <
  T extends FieldValues,
>(): UseFormDialogReturn<T> => {
  const [isOpen, setIsOpen] = useState(false);
  const [config, setConfig] = useState<FormDialogConfig<T>>({});
  const [resolver, setResolver] = useState<
    ((value: FormSubmissionData<T> | undefined) => void) | null
  >(null);

  const openFormDialog = (
    formConfig: FormDialogConfig<T>,
  ): Promise<FormSubmissionData<T> | undefined> => {
    return new Promise((resolve) => {
      setConfig(formConfig);
      setIsOpen(true);
      setResolver(() => resolve);
    });
  };

  const handleSubmit = (data: FormSubmissionData<T>) => {
    if (resolver) {
      resolver(data);
      setResolver(null);
      setIsOpen(false);
    }
  };

  const handleCancel = () => {
    if (resolver) {
      resolver(undefined);
      setResolver(null);
    }
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      if (resolver) {
        resolver(undefined);
      }
      setResolver(null);
    }
  };

  return {
    isOpen,
    config,
    openFormDialog,
    handleSubmit,
    handleCancel,
    handleOpenChange,
    formDialogProps: {
      open: isOpen,
      onOpenChange: handleOpenChange,
      title: config.title,
      description: config.description,
      submitButtonText: config.submitButtonText,
      cancelButtonText: config.cancelButtonText,
      fields: config.fields,
      sections: config.sections,
      defaultValues: config.defaultValues,
      layout: config.layout,
      onSubmit: handleSubmit,
      onCancel: handleCancel,
    },
  };
};

// ASSETS
